import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { SystemLanguageCodeModel } from './models/system-language-code-model';

@Injectable({
    providedIn: 'root',
})
export class SystemLanguageCodesAPIService extends BehaviorSubject<GridDataResult> {
    public baseUrlLocal: string = environment.apiBaseUrl;
    public loading: boolean = false;

    constructor(protected http: HttpClient) {
        super({ data: [], total: null });
    }

    public query(state: State, filter: CompositeFilterDescriptor): void {
        this.loading = true;
        this.fetch(state, filter).subscribe((x) => super.next(x));
    }

    protected fetch(state: State, filter: CompositeFilterDescriptor): Observable<GridDataResult> {
        let queryStart = '';
        let queryString = '';

        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pageindex=${state.skip}`);
        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pagesize=${state.take}`);

        const iso639_1Filter = this.getStateFilterValue(filter, 'languageCode_ISO639_1');
        if (iso639_1Filter != undefined && iso639_1Filter.length >= 1) {
            queryString = queryString.concat(`${queryStart}languageCode_ISO639_1=${encodeURIComponent(iso639_1Filter)}`);
        }

        const iso639_3Filter = this.getStateFilterValue(filter, 'languageCode_ISO639_3');
        if (iso639_3Filter != undefined && iso639_3Filter.length >= 1) {
            queryString = queryString.concat(`${queryStart}languageCode_ISO639_3=${encodeURIComponent(iso639_3Filter)}`);
        }

        const languageNameFilter = this.getStateFilterValue(filter, 'languageName');
        if (languageNameFilter != undefined && languageNameFilter.length >= 1) {
            queryString = queryString.concat(`${queryStart}languageName=${encodeURIComponent(languageNameFilter)}`);
        }

        return this.http.get<GridDataResult>(this.baseUrlLocal + `systemlanguagecodes/search` + queryString)
            .pipe(
                map((response: GridDataResult) => ({
                    data: response.data,
                    total: response.total
                })),
                tap(() => (this.loading = false))
            );
    }

    private getQueryStart(queryString: string): string {
        return queryString === '' ? '?' : '&';
    }

    private getStateFilterValue(filter: CompositeFilterDescriptor, field: string): string | undefined {
        if (!filter?.filters?.length) {
            return undefined;
        }
        const matchingFilter = filter.filters.find((item): item is FilterDescriptor => {
            return !('filters' in item) && item.field === field;
        });
        if (matchingFilter?.value === undefined || matchingFilter.value === null) {
            return undefined;
        }
        return String(matchingFilter.value);
    }
}

@Injectable()
export class SystemLanguageCodesService extends SystemLanguageCodesAPIService {
    constructor(http: HttpClient) {
        super(http);
    }

    public create(model: SystemLanguageCodeModel): Observable<SystemLanguageCodeModel> {
        return this.http.post<SystemLanguageCodeModel>(this.baseUrlLocal + `systemlanguagecodes`, model);
    }

    public update(id: number, model: SystemLanguageCodeModel): Observable<SystemLanguageCodeModel> {
        return this.http.put<SystemLanguageCodeModel>(this.baseUrlLocal + `systemlanguagecodes/${id}`, model);
    }

    public delete(id: number): Observable<void> {
        return this.http.delete<void>(this.baseUrlLocal + `systemlanguagecodes/${id}`);
    }
}
